import React, { useRef, useState } from 'react';
import {
  defaultCellRenderer,
} from "../../../../other/utils/utils.jsx";
import clsx from "clsx";
import {
  ALERT_ENTITIES,
  ALERT_TYPE_NAMES,
  ALERT_TYPES,
  ASYNC_TASK_STATUS,
  REMINDER_STATE,
  ReminderTypes,
  VIEW_TYPES
} from "../../../../other/constants/OtherKeys.jsx";
import { SWIVU_NOTIFY_COLOR_ID, swivuAlertBellActiveCls } from "../../../../other/configs/globalConfig.jsx";
import { getAlertBell } from "../../../../other/utils/utils.jsx";
import bgContrastHelper from "../../../../other/utils/bgUtils.jsx";
import { EDITOR_KEYS } from "../../../../other/constants/EditorKeys.jsx";
import { MessageDialog } from "@bryntum/schedulerpro";
import { asyncTaskResult } from "@/network/network.jsx";
import { getApiErrorMessage } from "@/integrations";
import { IMPORT_TYPE_NAMES } from "@/other/utils/otherUtils.jsx";

const getBgColorIfTask = (alertItem) => {
  if ( alertItem.entityType === ReminderTypes.task) {
    const taskEntity = alertItem.entity;
    if (taskEntity) {
      const contrastColor = bgContrastHelper(taskEntity.color, "#fff", "#000");
      return { backgroundColor: taskEntity.color, color: contrastColor };
    }
  }
  return {};
}

const getBannerTagColor = (alertType) => {
  const colors = {
    [ALERT_TYPES.REMINDER]: 'text-[hsl(30_67%_45%)]', // Amber/Orange
    [ALERT_TYPES.Schedule]: 'text-[hsl(330_50%_57%)]', // Pink
    [ALERT_TYPES.Checklist]: 'text-[hsl(90_60%_35%)]', // Green
    [ALERT_TYPES.Nudge]: 'text-[hsl(90_60%_35%)]', // Green
    [ALERT_TYPES.EntityStatus]: 'text-[hsl(195_60%_70%)]', // Light blue
    [ALERT_TYPES.NOTE]: 'text-[hsl(60_70%_35%)]', // Yellow-green
    [ALERT_TYPES.URGENT]: 'text-[hsl(4_75%_68%)]', // Red
  };
  return colors[alertType] || '';
}

const AlertNotificationBannerItem = ({ alertItem, editorHandler, onToggleBell, onCancelTask, viewManager, entityManager, onTaskDoubleClick, isAsyncTask }) => {
  const containerRef = useRef();
  const [_descriptionExpanded, _setDescriptionExpanded] = useState(false);

  const onOpenEntityInEditorHandler = () => {
    if (alertItem.description === "Failed") {
      asyncTaskResult(alertItem.asyncTask)
        .then((err) => {
          MessageDialog.alert({
            title: "Reason for Failure",
            message: getApiErrorMessage(err, "Unknown Reason")
          })
        })
        .catch((err) => {
          MessageDialog.alert({
            title: "Reason for Failure",
            message: getApiErrorMessage(err, "Unknown Reason")
          })
        })
      return;
    }

    // Marketing generation banners — delegate to processCompletedTask which
    // navigates to the appropriate page for each task type.
    if (
      alertItem.alertType === ALERT_TYPES.AsyncTask &&
      (alertItem.alertTag === "Marketing Manifest Generation" ||
        alertItem.alertTag === "Marketing Tags Generation" ||
        alertItem.alertTag === "Marketing Work File Generation" ||
        alertItem.alertTag === "Marketing Page Generation" ||
        alertItem.alertTag === "Document Generation" ||
        alertItem.alertTag === "Marketing Document Generation" ||
        alertItem.alertTag === "Site Publish" ||
        alertItem.title === "Site Publish" ||
        alertItem.title === "Site Publishing Started")
    ) {
      onTaskDoubleClick && onTaskDoubleClick(alertItem);
      return;
    }

    // Check if this is an incentive banner and show dialog instead of opening editor
    if (alertItem.alertType === ALERT_TYPES.AsyncTask && alertItem.alertTag === "Incentive Evaluation") {
      asyncTaskResult(alertItem.asyncTask).then((response) => {
        if (response.status === 200) {
          const incentiveId = response.data.incentive_id;
          if (incentiveId) {
            viewManager.selectView(viewManager.getDefaultViewByType(VIEW_TYPES.incentivePoints),  { canNavigate: true, forceLoadView: true }, { filters: { incentiveIds: [incentiveId] }});
          }
        }
      })
      return;
    }

    if (isAsyncTask && alertItem.description === ASYNC_TASK_STATUS.COMPLETED) {
      onTaskDoubleClick && onTaskDoubleClick(alertItem);
      return;
    }

    const editor = ALERT_ENTITIES[alertItem.entityType];
    if (editor) {
      entityManager.openEntityManager({ entityType: editor.entityManagerPage, entityId: alertItem.entity.id });
    } else if (alertItem.alertType === ALERT_TYPES.BoardAlert) {
      editorHandler.openEditor(EDITOR_KEYS.settings, null, {
        selectTabByTypeOnOpen: 'alertNotifications'
      });
    }
  };
  // const onMouseEnterHandler = (e) => {
  //   _onDisplayAlertItem({
  //     alertItem,
  //     boundingClientRect: e.currentTarget.getBoundingClientRect(),
  //   })
  // }
  //
  // const onMouseLeaveHandler = () => {
  //   _onDisplayAlertItem(null)
  // }

  // do not assign font icons with in css file as after the build vite remove all instances of font icons where the icon value starts with \f
  // see issue detail in notion https://www.notion.so/uak/Development-Issues-10621ce8dc908046b968d70be713a699?pvs=4
  // do not assign any font icon with in css as vite mishandle that due to its utf8 character set definition (note changing the charset of vite ascii get everything work again)
  // see vite github issue on this => https://github.com/vitejs/vite/issues/13676
  const onEyeOnMouseOverOut = (e, isMouseOver) => {
    const targetEl = e.target;
    if (targetEl.classList.contains(swivuAlertBellActiveCls)) {
      targetEl.classList.remove(isMouseOver ? "fa-eye": "fa-eye-slash");
      targetEl.classList.add(isMouseOver ? "fa-eye-slash": "fa-eye");
    }
  }

  const onCancelClickHandler = (alertItem) => {
    MessageDialog.confirm({
      title: "Confirm Cancellation",
      message: "Are you sure you want to cancel this async operation?"
    }).then((response) => {
      if (response === MessageDialog.okButton) {
        onCancelTask(alertItem);
      }
    });
  }

  const renderAsEntityNameOfBanner = (alertItem) => {
    if (alertItem.alertType === ALERT_TYPES.AsyncTask && alertItem?.boardId) {
      if (alertItem?.alertTag === "Incentive Evaluation") {
        return "Point";
      }

      if (alertItem?.alertTag?.includes("Bulk") && alertItem?.alertTag?.includes("Copy")) { // we don't have a deterministic way to identify type of entity copied
        return "Bulk Copy";
      }

      if (
        alertItem?.alertTag === "Marketing Manifest Generation" ||
        alertItem?.alertTag === "Marketing Tags Generation" ||
        alertItem?.alertTag === "Marketing Work File Generation" ||
        alertItem?.alertTag === "Marketing Page Generation" ||
        alertItem?.alertTag === "Document Generation" ||
        alertItem?.alertTag === "Marketing Document Generation"
      ) {
        return "Marketing";
      }

      if (
        alertItem?.alertTag === "Site Publish" ||
        alertItem?.title === "Site Publish" ||
        alertItem?.title === "Site Publishing Started"
      ) {
        return "Marketing";
      }

      if (
        alertItem?.alertTag?.toLowerCase().includes("board context") ||
        alertItem?.title?.toLowerCase().includes("board context")
      ) {
        return "Context";
      }

      return IMPORT_TYPE_NAMES[alertItem.dataType] ?? alertItem?.alertTag ?? "Task";
    } else if (alertItem?.asEntityType  === "Board User") {
      return "Board Alert"
    } else {
      return alertItem?.asEntityType
    }
  }

  const renderAlertTagOfBanner = (alertItem) => {
    if (alertItem.alertType === ALERT_TYPES.AsyncTask) {
      if (alertItem.alertTag === "Incentive Evaluation") {
        return "Execution"
      }
      return "Status"
    } else if (alertItem.alertTag === "Board Alert") {
      return "Alert Description"
    } else {
      return alertItem.alertTag || ALERT_TYPE_NAMES[alertItem.alertType]
    }
  }

  return (
    <div className={`relative ${SWIVU_NOTIFY_COLOR_ID[alertItem.alertType]}`} ref={containerRef} draggable={false}>
      <div className="flex min-w-96 select-none bg-background">
        {alertItem.entityType === ReminderTypes.task &&  <div className="flex items-center p-0 w-1.5 h-auto" style={getBgColorIfTask(alertItem)}></div>}
        <div className="flex justify-between w-full items-center px-4 py-1 text-foreground border dark:border-0 rounded-md">
          <div className="flex">
            <div className={clsx(
              "cursor-pointer whitespace-nowrap w-full",
              _descriptionExpanded ? "max-w-3xl" : ""
            )} role="button" onDoubleClick={onOpenEntityInEditorHandler}>
              {renderAsEntityNameOfBanner(alertItem)}: {defaultCellRenderer({ value: alertItem.entity?.name || alertItem?.title })}<br />
              <span className={getBannerTagColor(alertItem.alertType)}>{renderAlertTagOfBanner(alertItem)}: </span> {defaultCellRenderer({ value: alertItem.description })}
            </div>
          </div>
          {alertItem.alertType === ALERT_TYPES.AsyncTask && alertItem.description === "Pending" ? (
            <div className="rounded-full p-1 px-2 cursor-pointer ml-1 text-xl" role="button" onClick={() => onCancelClickHandler(alertItem)}>
            <span
              onMouseOver={(e) => onEyeOnMouseOverOut(e, true)}
              onMouseOut={(e) => onEyeOnMouseOverOut(e, false)}
              dangerouslySetInnerHTML={{ __html: getAlertBell({
                  reminderState: REMINDER_STATE.NOT_SEEN,
                  isCancelIcon: true
                }) }}
            />
            </div>
          ) : (
            <div className="rounded-full p-1 px-2 cursor-pointer ml-1 text-xl" role="button" onClick={onToggleBell}>
            <span
              onMouseOver={(e) => onEyeOnMouseOverOut(e, true)}
              onMouseOut={(e) => onEyeOnMouseOverOut(e, false)}
              dangerouslySetInnerHTML={{ __html: getAlertBell({
                  reminderState: REMINDER_STATE.NOT_SEEN,
                  isAlertBell: alertItem.alertType === ALERT_TYPES.REMINDER
                }) }}
            />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertNotificationBannerItem;
